/* BuildTypePopup.jsx
 *
 * Interactive popup launched when a user clicks a build-type chip
 * under the homepage hero search. Shows:
 *   1. Contextual MiniMatcher launchers ("Find a New Home Builder" etc.)
 *   2. Curated information pages they can deep-link into
 *   3. A clear primary CTA to the full build-type guide page
 *
 * Mounts at document.body via portal so the slide-up panel sits above
 * the search dropdown and the page mesh background.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { X, ChevronRight } from 'lucide-react';
import MiniMatcher from './MiniMatcher';
import { resolveBuildTypePopup } from './buildTypePopupConfig';
import MatchDisclaimer from '../trust/MatchDisclaimer';
import './buildTypePopup.css';

export default function BuildTypePopup({ chip, onClose, embedded = false }) {
  const cfg = resolveBuildTypePopup(chip?.href);
  const [matcher, setMatcher] = useState(null); // { category, label }
  const navigate = useNavigate();

  if (!chip) return null;

  // Fallback path, no config registered yet, render a tiny shell so
  // the user still has a way through. We never want a dead chip.
  const eyebrow = cfg?.eyebrow || 'Build type';
  const title = cfg?.title || chip.name;
  const subtitle = cfg?.subtitle || `Smart matches and key resources for ${chip.name.toLowerCase()}.`;
  const matchers = cfg?.matchers || [];
  const resources = cfg?.resources || [];
  const explore = cfg?.explore || { label: `Read the ${chip.name.toLowerCase()} guide`, href: chip.href };
  const primaryAction = cfg?.primaryAction || null;

  // 2026-02-24: When `embedded=true`, BuildTypePopup is rendered INSIDE
  // the JourneyModal as a wizard step. Skip the overlay / scrim / header
  // / portal because JourneyModal owns the frame. Just emit the body
  // sections + footer so we have a single modal flow rather than two
  // stacked modals.
  if (embedded) {
    return (
      <>
        <div className="bp-btp-body" data-testid={`build-type-popup-embedded-${chip.href.replace(/[^a-z0-9]+/gi, '-')}`}>
          {matchers.length > 0 && (
            <section className="bp-btp-section">
              <div className="bp-btp-section-head">
                <h4 className="bp-btp-section-title">BuildPilot Intelligence suggestions</h4>
                <p className="bp-btp-section-sub" data-testid="btp-suggestions-disclaimer" style={{ margin: '2px 0 0', fontSize: 11, color: '#64748b' }}>
                  Suggestions only · not endorsements
                </p>
              </div>
              <div className="bp-btp-matchers">
                {matchers.map((m) => (
                  <button
                    key={m.category}
                    type="button"
                    className="bp-btp-matcher"
                    onClick={() => {
                      if (m.linkTo) {
                        onClose();
                        navigate(m.linkTo);
                      } else {
                        setMatcher(m);
                      }
                    }}
                    data-testid={`build-type-popup-matcher-${m.category.replace(':', '-')}`}
                  >
                    <span className="bp-btp-matcher-label">{m.label}</span>
                    <ChevronRight size={14} className="bp-btp-matcher-arrow" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {resources.length > 0 && (
            <section className="bp-btp-section">
              <div className="bp-btp-section-head">
                <h4 className="bp-btp-section-title">Key resources</h4>
              </div>
              <ul className="bp-btp-resources">
                {resources.map((r) => (
                  <li key={r.href}>
                    <Link
                      to={r.href}
                      className="bp-btp-resource"
                      onClick={onClose}
                      data-testid={`build-type-popup-resource-${r.href.replace(/[^a-z0-9]+/gi, '-')}`}
                    >
                      <span className="bp-btp-resource-title">{r.title}</span>
                      {r.body && <span className="bp-btp-resource-body">{r.body}</span>}
                      <ChevronRight size={14} className="bp-btp-resource-arrow" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="bp-btp-foot">
            {primaryAction && (
              <Link
                to={primaryAction.href}
                className="bp-btp-primary-action"
                onClick={onClose}
                data-testid="build-type-popup-primary-action"
              >
                {primaryAction.label}
                <ChevronRight size={16} />
              </Link>
            )}
            <Link
              to={explore.href}
              className="bp-btp-explore"
              onClick={onClose}
              data-testid="build-type-popup-explore"
            >
              {explore.label}
              <ChevronRight size={16} />
            </Link>
            <MatchDisclaimer inline className="!text-[10.5px] !leading-[1.45] mt-2 text-center" />
          </div>
        </div>

        {/* Nested MiniMatcher still opens above (its own portal). */}
        <MiniMatcher
          open={!!matcher}
          onClose={() => setMatcher(null)}
          category={matcher?.category}
          heading={matcher?.label}
          sourcePlacement={`build-type-popup-${chip.href.replace(/[^a-z0-9]+/gi, '-')}`}
          sourcePage="/"
        />
      </>
    );
  }

  const node = (
    <div className="bp-btp-overlay" role="dialog" aria-modal="true" data-testid="build-type-popup">
      <button type="button" className="bp-btp-scrim" aria-label="Close" onClick={onClose} />

      <aside className="bp-btp-panel" data-testid={`build-type-popup-${chip.href.replace(/[^a-z0-9]+/gi, '-')}`}>
        <header className="bp-btp-head">
          <div className="bp-btp-head-icon" aria-hidden="true">
            {chip.iconSrc && <img src={chip.iconSrc} alt="" aria-hidden="true" loading="lazy" />}
          </div>
          <div className="bp-btp-head-text">
            <p className="bp-btp-eyebrow">{eyebrow}</p>
            <h3 className="bp-btp-title">{title}</h3>
            <p className="bp-btp-sub">{subtitle}</p>
          </div>
          <button type="button" className="bp-btp-close" onClick={onClose} aria-label="Close" data-testid="build-type-popup-close">
            <X size={18} />
          </button>
        </header>

        <div className="bp-btp-body">
          {matchers.length > 0 && (
            <section className="bp-btp-section">
              <div className="bp-btp-section-head">
                <h4 className="bp-btp-section-title">BuildPilot Intelligence suggestions</h4>
                <p className="bp-btp-section-sub" data-testid="btp-suggestions-disclaimer" style={{ margin: '2px 0 0', fontSize: 11, color: '#64748b' }}>
                  Suggestions only · not endorsements
                </p>
              </div>
              <div className="bp-btp-matchers">
                {matchers.map((m) => (
                  <button
                    key={m.category}
                    type="button"
                    className="bp-btp-matcher"
                    onClick={() => {
                      if (m.linkTo) {
                        onClose();
                        navigate(m.linkTo);
                      } else {
                        setMatcher(m);
                      }
                    }}
                    data-testid={`build-type-popup-matcher-${m.category.replace(':', '-')}`}
                  >
                    <span className="bp-btp-matcher-label">{m.label}</span>
                    <ChevronRight size={14} className="bp-btp-matcher-arrow" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {resources.length > 0 && (
            <section className="bp-btp-section">
              <div className="bp-btp-section-head">
                <h4 className="bp-btp-section-title">Key resources</h4>
              </div>
              <ul className="bp-btp-resources">
                {resources.map((r) => (
                  <li key={r.href}>
                    <Link
                      to={r.href}
                      className="bp-btp-resource"
                      onClick={onClose}
                      data-testid={`build-type-popup-resource-${r.href.replace(/[^a-z0-9]+/gi, '-')}`}
                    >
                      <span className="bp-btp-resource-title">{r.title}</span>
                      {r.body && <span className="bp-btp-resource-body">{r.body}</span>}
                      <ChevronRight size={14} className="bp-btp-resource-arrow" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <footer className="bp-btp-foot">
          {primaryAction && (
            <Link
              to={primaryAction.href}
              className="bp-btp-primary-action"
              onClick={onClose}
              data-testid="build-type-popup-primary-action"
            >
              {primaryAction.label}
              <ChevronRight size={16} />
            </Link>
          )}
          <Link
            to={explore.href}
            className="bp-btp-explore"
            onClick={onClose}
            data-testid="build-type-popup-explore"
          >
            {explore.label}
            <ChevronRight size={16} />
          </Link>
          <MatchDisclaimer inline className="!text-[10.5px] !leading-[1.45] mt-2 text-center" />
        </footer>
      </aside>

      {/* Nested MiniMatcher, opens above this popup when a matcher
          row is clicked. Renders its own portal so we don't fight
          stacking contexts. */}
      <MiniMatcher
        open={!!matcher}
        onClose={() => setMatcher(null)}
        category={matcher?.category}
        heading={matcher?.label}
        sourcePlacement={`build-type-popup-${chip.href.replace(/[^a-z0-9]+/gi, '-')}`}
        sourcePage="/"
      />
    </div>
  );

  if (typeof document === 'undefined') return node;
  return ReactDOM.createPortal(node, document.body);
}
